import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Toast } from 'primereact/toast';
import { apiUrl } from '../services/api';
import PageLoader from '../components/PageLoader';
import "./PropertyLedger.css";

const INCOME_CATEGORIES = ["rent", "late_fee", "pet_fee", "parking", "laundry", "other"];
const EXPENSE_CATEGORIES = [
  "mortgage", "insurance", "property_tax", "repair", "maintenance",
  "management", "utilities", "hoa", "vacancy", "capex", "other", "owner_distribution",
];

function fmt(val) {
  if (val == null || val === "") return "—";
  return "$" + Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function BlankIncomeRow() {
  return { id: null, period_month: "", amount: "", category: "rent", notes: "", _new: true, _editing: true };
}
function BlankExpenseRow() {
  return { id: null, expense_date: "", amount: "", category: "repair", vendor: "", description: "", _new: true, _editing: true };
}

// ── Inline-editable income row ────────────────────────────────────────────────
function IncomeRow({ row, onSave, onDelete, onCancel }) {
  const [draft, setDraft] = useState({ ...row });
  const editing = row._editing;

  function set(field, val) { setDraft(d => ({ ...d, [field]: val })); }

  function handleSave() {
    if (!draft.period_month || !draft.amount) return;
    onSave(draft);
  }

  if (!editing) {
    return (
      <tr>
        <td>{row.period_month}</td>
        <td className="cap">{row.category}</td>
        <td className="money green">{fmt(row.amount)}</td>
        <td>{row.notes || "—"}</td>
        <td className="actions">
          <button className="act-btn act-edit" onClick={() => onSave({ ...row, _editing: true })}>Edit</button>
          <button className="act-btn act-del" onClick={() => onDelete(row.id)}>Delete</button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="editing-row">
      <td><input type="month" value={draft.period_month} onChange={e => set("period_month", e.target.value)} /></td>
      <td>
        <select value={draft.category} onChange={e => set("category", e.target.value)}>
          {INCOME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td><input type="number" min="0" step="0.01" value={draft.amount} onChange={e => set("amount", e.target.value)} placeholder="0.00" /></td>
      <td><input type="text" value={draft.notes} onChange={e => set("notes", e.target.value)} placeholder="Notes" /></td>
      <td className="actions">
        <button className="act-btn act-save" onClick={handleSave}>Save</button>
        <button className="act-btn act-cancel" onClick={() => onCancel(row)}>Cancel</button>
      </td>
    </tr>
  );
}

// ── Inline-editable expense row ───────────────────────────────────────────────
function ExpenseRow({ row, onSave, onDelete, onCancel }) {
  const [draft, setDraft] = useState({ ...row });
  const editing = row._editing;

  function set(field, val) { setDraft(d => ({ ...d, [field]: val })); }

  function handleSave() {
    if (!draft.expense_date || !draft.amount) return;
    onSave(draft);
  }

  if (!editing) {
    return (
      <tr>
        <td>{row.expense_date ? row.expense_date.slice(0, 10) : "—"}</td>
        <td className="cap">{row.category}</td>
        <td className="money red">{fmt(row.amount)}</td>
        <td>{row.vendor || "—"}</td>
        <td>{row.description || "—"}</td>
        <td className="actions">
          <button className="act-btn act-edit" onClick={() => onSave({ ...row, _editing: true })}>Edit</button>
          <button className="act-btn act-del" onClick={() => onDelete(row.id)}>Delete</button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="editing-row">
      <td><input type="date" value={draft.expense_date ? draft.expense_date.slice(0, 10) : ""} onChange={e => set("expense_date", e.target.value)} /></td>
      <td>
        <select value={draft.category} onChange={e => set("category", e.target.value)}>
          {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </td>
      <td><input type="number" min="0" step="0.01" value={draft.amount} onChange={e => set("amount", e.target.value)} placeholder="0.00" /></td>
      <td><input type="text" value={draft.vendor} onChange={e => set("vendor", e.target.value)} placeholder="Vendor" /></td>
      <td><input type="text" value={draft.description} onChange={e => set("description", e.target.value)} placeholder="Description" /></td>
      <td className="actions">
        <button className="act-btn act-save" onClick={handleSave}>Save</button>
        <button className="act-btn act-cancel" onClick={() => onCancel(row)}>Cancel</button>
      </td>
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PropertyLedger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [property, setProperty] = useState(null);
  const [income, setIncome] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [tab, setTab] = useState("income");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null); // { type, id }

  function showToast(msg, type = "success") {
    toast.current?.show({
      severity: type,
      summary: type === 'error' ? 'Error' : 'Success',
      detail: msg,
      life: type === 'error' ? 5000 : 3200,
    });
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [propRes, incRes, expRes] = await Promise.all([
        fetch(apiUrl(`/properties/${id}`), { headers }),
        fetch(apiUrl(`/properties/${id}/income`), { headers }),
        fetch(apiUrl(`/properties/${id}/expenses`), { headers }),
      ]);
      const [propData, incData, expData] = await Promise.all([propRes.json(), incRes.json(), expRes.json()]);
      setProperty(propData);
      setIncome((incData || []).map(r => ({ ...r, _editing: false })));
      setExpenses((expData || []).map(r => ({ ...r, _editing: false })));
    } catch {
      showToast("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // ── Income handlers ──────────────────────────────────────────────────────
  async function handleSaveIncome(row) {
    if (row._editing && !row._new) {
      // Toggle edit mode on without saving yet — re-render with editing flag
      setIncome(prev => prev.map(r => r.id === row.id ? { ...row } : r));
      return;
    }
    // Actual persist
    const body = {
      period_month: row.period_month,
      amount: parseFloat(row.amount),
      category: row.category,
      notes: row.notes,
    };
    try {
      if (row._new) {
        const res = await fetch(apiUrl(`/properties/${id}/income`), {
          method: "POST", headers, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
        showToast("Income added");
      } else {
        const res = await fetch(apiUrl(`/income/${row.id}`), {
          method: "PUT", headers, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
        showToast("Income updated");
      }
      await load();
    } catch {
      showToast("Save failed", "error");
    }
  }

  function handleCancelIncome(row) {
    if (row._new) {
      setIncome(prev => prev.filter(r => r !== row));
    } else {
      setIncome(prev => prev.map(r => r.id === row.id ? { ...r, _editing: false } : r));
    }
  }

  function handleDeleteIncome(rowId) {
    setConfirmDelete({ type: "income", id: rowId });
  }

  // ── Expense handlers ─────────────────────────────────────────────────────
  async function handleSaveExpense(row) {
    if (row._editing && !row._new) {
      setExpenses(prev => prev.map(r => r.id === row.id ? { ...row } : r));
      return;
    }
    const body = {
      expense_date: row.expense_date,
      amount: parseFloat(row.amount),
      category: row.category,
      vendor: row.vendor,
      description: row.description,
    };
    try {
      if (row._new) {
        const res = await fetch(apiUrl(`/properties/${id}/expenses`), {
          method: "POST", headers, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
        showToast("Expense added");
      } else {
        const res = await fetch(apiUrl(`/expenses/${row.id}`), {
          method: "PUT", headers, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error();
        showToast("Expense updated");
      }
      await load();
    } catch {
      showToast("Save failed", "error");
    }
  }

  function handleCancelExpense(row) {
    if (row._new) {
      setExpenses(prev => prev.filter(r => r !== row));
    } else {
      setExpenses(prev => prev.map(r => r.id === row.id ? { ...r, _editing: false } : r));
    }
  }

  function handleDeleteExpense(rowId) {
    setConfirmDelete({ type: "expense", id: rowId });
  }

  // ── Confirm delete ───────────────────────────────────────────────────────
  async function doDelete() {
    if (!confirmDelete) return;
    const { type, id: rowId } = confirmDelete;
    const url = type === "income"
      ? apiUrl(`/income/${rowId}`)
      : apiUrl(`/expenses/${rowId}`);
    try {
      const res = await fetch(url, { method: "DELETE", headers });
      if (!res.ok) throw new Error();
      showToast(`${type === "income" ? "Income" : "Expense"} deleted`);
      await load();
    } catch {
      showToast("Delete failed", "error");
    } finally {
      setConfirmDelete(null);
    }
  }

  // ── Totals ───────────────────────────────────────────────────────────────
  const totalIncome = income.filter(r => !r._new).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const totalExpenses = expenses.filter(r => !r._new).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const netCashFlow = totalIncome - totalExpenses;

  if (loading) return <PageLoader label="Loading ledger" />;

  return (
    <div className="ledger-page">
      <Toast ref={toast} position="top-right" />

      {/* Confirm modal */}
      {confirmDelete && (
        <div className="ledger-modal-backdrop">
          <div className="ledger-modal">
            <p>Delete this {confirmDelete.type} entry? This cannot be undone.</p>
            <div className="ledger-modal-actions">
              <button className="act-btn act-del" onClick={doDelete}>Delete</button>
              <button className="act-btn act-cancel" onClick={() => setConfirmDelete(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="ledger-header">
        <div>
          <button className="ledger-back" onClick={() => navigate(`/properties/${id}`)}>
            <i className="pi pi-arrow-left" /> Back to Property
          </button>
          <h1 className="ledger-title">
            Ledger — {property?.nickname || property?.address_line1 || "Property"}
          </h1>
          <p className="ledger-address">{property?.address_line1}, {property?.city}, {property?.state}</p>
        </div>
        <div className="ledger-summary">
          <div className="ledger-sum-item">
            <span className="ledger-sum-label">Total Income</span>
            <span className="ledger-sum-val green">{fmt(totalIncome)}</span>
          </div>
          <div className="ledger-sum-item">
            <span className="ledger-sum-label">Total Expenses</span>
            <span className="ledger-sum-val red">{fmt(totalExpenses)}</span>
          </div>
          <div className="ledger-sum-item">
            <span className="ledger-sum-label">Net Cash Flow</span>
            <span className={`ledger-sum-val ${netCashFlow >= 0 ? "green" : "red"}`}>{fmt(netCashFlow)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="ledger-tabs">
        <button className={`ledger-tab ${tab === "income" ? "active" : ""}`} onClick={() => setTab("income")}>
          Income <span className="ledger-tab-count">{income.filter(r => !r._new).length}</span>
        </button>
        <button className={`ledger-tab ${tab === "expenses" ? "active" : ""}`} onClick={() => setTab("expenses")}>
          Expenses <span className="ledger-tab-count">{expenses.filter(r => !r._new).length}</span>
        </button>
      </div>

      {/* Income table */}
      {tab === "income" && (
        <div className="ledger-section">
          <div className="ledger-section-header">
            <span className="ledger-section-title">Income Records</span>
            <button
              className="ledger-add-btn"
              onClick={() => setIncome(prev => [...prev, BlankIncomeRow()])}
            >
              <i className="pi pi-plus" /> Add Income
            </button>
          </div>
          {income.length === 0
            ? <p className="ledger-empty">No income records yet. Click "Add Income" to start.</p>
            : (
              <div className="ledger-table-wrap">
                <table className="ledger-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {income.map((row, i) => (
                      <IncomeRow
                        key={row.id ?? `new-${i}`}
                        row={row}
                        onSave={handleSaveIncome}
                        onDelete={handleDeleteIncome}
                        onCancel={handleCancelIncome}
                      />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} className="ledger-foot-label">Total</td>
                      <td className="money green ledger-foot-val">{fmt(totalIncome)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          }
        </div>
      )}

      {/* Expenses table */}
      {tab === "expenses" && (
        <div className="ledger-section">
          <div className="ledger-section-header">
            <span className="ledger-section-title">Expense Records</span>
            <button
              className="ledger-add-btn"
              onClick={() => setExpenses(prev => [...prev, BlankExpenseRow()])}
            >
              <i className="pi pi-plus" /> Add Expense
            </button>
          </div>
          {expenses.length === 0
            ? <p className="ledger-empty">No expense records yet. Click "Add Expense" to start.</p>
            : (
              <div className="ledger-table-wrap">
                <table className="ledger-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Vendor</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((row, i) => (
                      <ExpenseRow
                        key={row.id ?? `new-${i}`}
                        row={row}
                        onSave={handleSaveExpense}
                        onDelete={handleDeleteExpense}
                        onCancel={handleCancelExpense}
                      />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2} className="ledger-foot-label">Total</td>
                      <td className="money red ledger-foot-val">{fmt(totalExpenses)}</td>
                      <td colSpan={3} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          }
        </div>
      )}
    </div>
  );
}
